import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, KeyRound, CheckCircle2 } from "lucide-react";

function AuthShell({ children }) {
    const b = useBranding();
    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <BrandLogo size={32} />
                    <span className="font-display font-black text-xl">{b.short_name}</span>
                </Link>
                <div className="bg-white border border-border p-8 space-y-6">{children}</div>
            </div>
        </div>
    );
}

export function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try { await api.post("/auth/password/forgot", { email }); setSent(true); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <AuthShell>
            {sent ? (
                <div className="text-center space-y-3">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                    <h1 className="font-display font-black text-2xl">Check your inbox</h1>
                    <p className="text-sm text-foreground/80">If an account exists for <strong>{email}</strong>, we've sent a reset link. It expires in 2 hours.</p>
                    <Link to="/login" className="text-xs font-mono uppercase tracking-widest text-ikb hover:underline">Back to sign in →</Link>
                </div>
            ) : (
                <>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Forgot password</div>
                        <h1 className="font-display font-black text-3xl mt-2">Reset password</h1>
                        <p className="text-sm text-foreground/80 mt-2">Enter your email — we'll send a secure link.</p>
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Email</Label>
                            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-2 h-11" data-testid="forgot-email" />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11" data-testid="forgot-submit">
                            <Mail className="w-4 h-4 mr-2" /> {busy ? "Sending..." : "Send reset link"}
                        </Button>
                    </form>
                    <div className="text-xs text-foreground/80 text-center">
                        Remembered it? <Link to="/login" className="text-ikb hover:underline">Sign in →</Link>
                    </div>
                </>
            )}
        </AuthShell>
    );
}

export function ResetPassword() {
    const { token } = useParams();
    const nav = useNavigate();
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        (async () => {
            try { const { data } = await api.get(`/auth/password/reset/${token}`); setPreview(data); }
            catch (e) { setError(formatApiError(e)); }
        })();
    }, [token]);
    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 6) { toast.error("Password must be 6+ characters"); return; }
        if (pwd !== confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/password/reset", { token, password: pwd });
            toast.success("Password reset — signed in");
            const role = data?.user?.role;
            if (role === "seeker") nav("/seeker/jobs");
            else if (role === "admin") nav("/admin");
            else nav("/dashboard");
            // Force reload to refresh AuthContext
            setTimeout(() => window.location.reload(), 100);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <AuthShell>
            {error ? (
                <div className="text-center space-y-3">
                    <h1 className="font-display font-black text-2xl">Link invalid</h1>
                    <p className="text-sm text-foreground/80">{error}</p>
                    <Link to="/forgot-password" className="text-xs font-mono uppercase tracking-widest text-ikb hover:underline">Request a new link →</Link>
                </div>
            ) : !preview ? (
                <div className="text-sm font-mono text-foreground/80">Loading...</div>
            ) : (
                <>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Reset password</div>
                        <h1 className="font-display font-black text-3xl mt-2">Set new password</h1>
                        <p className="text-sm text-foreground/80 mt-2">For <strong>{preview.email}</strong></p>
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">New password</Label>
                            <Input type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} className="rounded-none mt-2 h-11" data-testid="reset-pwd" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Confirm</Label>
                            <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none mt-2 h-11" data-testid="reset-pwd-confirm" />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11" data-testid="reset-submit">
                            <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Updating..." : "Set new password"}
                        </Button>
                    </form>
                </>
            )}
        </AuthShell>
    );
}
